import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../../services/api';
import { Button } from '../../../components/ui/Button';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

interface OAuthConnectSectionProps {
  accountId: number;
  oauthProvider?: string | null;
  onConnected?: () => void;
  /** embedded: inside form section (no duplicate title/card). standalone: detail page card. */
  variant?: 'embedded' | 'standalone';
}

function extractApiErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const message = (err as { response?: { data?: { message?: string | string[] } } }).response?.data
      ?.message;
    if (Array.isArray(message)) return message.join(', ');
    if (typeof message === 'string' && message.trim()) return message;
  }
  return 'Failed to start OAuth connection';
}

const OAuthConnectSection: React.FC<OAuthConnectSectionProps> = ({
  accountId,
  oauthProvider,
  onConnected,
  variant = 'standalone',
}) => {
  const { colors } = useTheme();
  const [loading, setLoading] = useState<'google' | 'microsoft' | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [oauthConnected, setOauthConnected] = useState(false);

  useEffect(() => {
    if (searchParams.get('oauth') !== 'connected') return;
    setOauthConnected(true);
    onConnected?.();
    const next = new URLSearchParams(searchParams);
    next.delete('oauth');
    setSearchParams(next, { replace: true });
    // Run once when OAuth callback lands with ?oauth=connected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const connect = async (provider: 'google' | 'microsoft') => {
    if (accountId <= 0) return;
    setConnectError(null);
    setLoading(provider);
    try {
      const res = await api.get(`/email-accounts/oauth/${provider}/connect`, {
        params: { accountId },
      });
      const url = res.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      setConnectError('No OAuth redirect URL returned.');
    } catch (err: unknown) {
      setConnectError(extractApiErrorMessage(err));
    } finally {
      setLoading(null);
    }
  };

  const content = (
    <div className="space-y-3">
      {oauthConnected && (
        <div
          role="status"
          className="flex items-center justify-between gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-700 dark:bg-green-900/20 dark:text-green-300"
        >
          <span>OAuth account connected successfully.</span>
          <button
            type="button"
            onClick={() => setOauthConnected(false)}
            className="font-semibold hover:underline"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
      {connectError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          {connectError}
        </div>
      )}
      {variant === 'standalone' && (
        <h3 className={cn('text-sm font-semibold', colors.text)}>OAuth2 (XOAUTH2)</h3>
      )}
      <p className={cn('text-sm', colors.textMuted)}>
        Connect Google or Microsoft to use OAuth instead of app passwords for IMAP/SMTP.
        {oauthProvider ? (
          <span className="block mt-1">
            Connected: <strong>{oauthProvider}</strong>
          </span>
        ) : null}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          disabled={loading !== null || accountId <= 0}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void connect('google');
          }}
        >
          {loading === 'google' ? 'Redirecting...' : 'Connect Google'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={loading !== null || accountId <= 0}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void connect('microsoft');
          }}
        >
          {loading === 'microsoft' ? 'Redirecting...' : 'Connect Microsoft'}
        </Button>
      </div>
    </div>
  );
  if (variant === 'embedded') {
    return content;
  }

  return (
    <div className={cn('rounded-lg border p-4 space-y-3', colors.border, colors.surface)}>
      {content}
    </div>
  );
};

export default OAuthConnectSection;
